import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { odataParamsFactory, toastFactory } from '@tdw/core';
import { withLatestFrom, switchMap, map, catchError, of, tap, filter } from 'rxjs';
import { fittingsAdminFilterFactory } from 'src/app/models';
import { ConfigurationsService } from 'src/app/services/configurations.service';
import { FittingsService } from 'src/app/services/fittings.service';
import { MonitoringService } from 'src/app/services/monitoring.service';

import * as fromStore from '../../../store';

import { Router } from '@angular/router';

@Injectable()
export class FittingsAdminEffects {


  loadFittings$ = createEffect(() => this.actions$.pipe(
    ofType(fromStore.actions.ADMIN.loadFittings, fromStore.actions.ADMIN.loadFittingsForPage, fromStore.actions.ADMIN.updateFittingsAdminFilter),
    withLatestFrom(this.store.select(fromStore.selectors.ADMIN.selectFittingQuery), this.store.select(fromStore.selectors.ADMIN.selectFittingAdminFilter)),
    switchMap(([action, q, adminFilter]) => this.fittingService.getFittingMaintenance(odataParamsFactory({
      ...q,
      filter: fittingsAdminFilterFactory(adminFilter).toOdataFilter()
    }))
      .pipe(map(fittings => fromStore.actions.ADMIN.loadFittingsSuccess({ fittings })),
        catchError(error => of(fromStore.actions.ADMIN.loadFittingsFail({ error })))))));

  loadConfigurations$ = createEffect(() => this.actions$.pipe(
    ofType(fromStore.actions.ADMIN.loadConfigurations, fromStore.actions.ADMIN.loadConfigurationsForPage, fromStore.actions.ADMIN.changeFilterValue, fromStore.actions.ADMIN.saveProductConfigurationSuccess),
    withLatestFrom(this.store.select(fromStore.selectors.ADMIN.selectConfigurationQuery)),
    switchMap(([action, q]) => this.configurationService.getConfigurations(odataParamsFactory(q))
      .pipe(map(configurations => fromStore.actions.ADMIN.loadConfigurationsSuccess({ configurations })),
        catchError(error => of(fromStore.actions.ADMIN.loadConfigurationsFail({ error })))))));

  saveProductConfiguration$ = createEffect(() => this.actions$.pipe(
    ofType(fromStore.actions.ADMIN.saveProductConfiguration),
    switchMap(action => this.configurationService.saveProductConfiguration(action.configuration)
      .pipe(map(res => fromStore.actions.ADMIN.saveProductConfigurationSuccess()),
        tap(res => this.monitoringService.logEvent('ProductConfiguration Update', action.configuration)),
        catchError(error => of(fromStore.actions.ADMIN.saveProductConfigurationFail({ error })))))));

  showToastAfterSave$ = createEffect(() => this.actions$.pipe(
    ofType(fromStore.actions.ADMIN.saveProductConfigurationSuccess),
    map(res => fromStore.actions.MESSAGES.addToast({
      toast: toastFactory({
        class: 'success',
        message: 'Product Configuration was updated',
        title: 'Product Config'
      })
    }))
  ));

  loadCurrentlyEditingFitting$ = createEffect(() => this.actions$.pipe(
    ofType(
      fromStore.actions.ADMIN.loadCurrentlyEditingFitting,
      fromStore.actions.ADMIN.addFittingDesignCodeSuccess,
      fromStore.actions.ADMIN.removeFittingDesignCodeSuccess,
      fromStore.actions.ADMIN.addFittingDesignPressureSuccess,
      fromStore.actions.ADMIN.removeFittingDesignPressureSuccess,
      fromStore.actions.ADMIN.addAssociatedProductSuccessful,
      fromStore.actions.ADMIN.removeAssociatedProductSuccess,
      fromStore.actions.ADMIN.uploadDocumentSuccess
    ),
    withLatestFrom(this.store.select(fromStore.selectors.ADMIN.selectCurrentlyEditingFitting)),
    map(([action, currentlyEditingFitting]) => {
      if (action.type === fromStore.actions.ADMIN.loadCurrentlyEditingFitting.type) return action.fittingId;
      return currentlyEditingFitting?.id;
    }),
    filter(f => !!f),
    switchMap(id => this.fittingService.getFittingById(id || -1)
      .pipe(map(fitting => fromStore.actions.ADMIN.loadCurrentlyEditingFittingSuccess({ fitting })),
        catchError(error => of(fromStore.actions.ADMIN.loadCurrentlyEditingFittingFail({ error }))))
    )
  ));

  loadCurrentlyEditingAssociatedProducts$ = createEffect(() => this.actions$.pipe(
    ofType(fromStore.actions.ADMIN.loadCurrentlyEditingFittingSuccess),
    map(action => (action.fitting?.fittingProductAssociatedProducts || [])?.map(p => p.associatedProductId)),
    map(ids => (ids || []).length > 0 ? ids.map(m => m.toString()).join(',') : '-1'),
    switchMap(ids => this.fittingService.getFittingAssociatedProducts(odataParamsFactory({
      filter: `id in (${ids})`,
      select: ['id', 'AssociatedProductTypeId', 'ProductNumber', 'ProductName', 'Description', 'CreatedDate', 'CreatedBy', 'UpdatedDate', 'UpdatedBy'],
      expand: 'associatedProductType'
    }))
      .pipe(map(associatedProducts => fromStore.actions.ADMIN.loadCurrentlyEditingAssociatedProductsSuccess({ associatedProducts: associatedProducts || [] })),
        catchError(error => of(fromStore.actions.ADMIN.loadCurrentlyEditingFittingFail({ error })))))
  ));

  monitorErrors$ = createEffect(() => this.actions$.pipe(
    ofType(
      fromStore.actions.ADMIN.saveProductConfigurationFail,
      fromStore.actions.ADMIN.loadFittingsFail,
      fromStore.actions.ADMIN.loadConfigurationsFail),
    tap(error => this.monitoringService.logException(error.error)),
    map(error => fromStore.actions.ERRORS.addGlobalError({ error: error.error }))
  ));

  saveFitting$ = createEffect(() => this.actions$.pipe(
    ofType(fromStore.actions.ADMIN.saveFittingRecord),
    switchMap(action => this.fittingService.saveFitting(action.fittingProduct)
      .pipe(map(() => fromStore.actions.ADMIN.saveFittingRecordSuccess()),
        catchError(error => of(fromStore.actions.ADMIN.saveFittingRecordFail({ error }))))
    )
  ));

  reloadAfterSave$ = createEffect(() => this.actions$.pipe(
    ofType(fromStore.actions.ADMIN.saveFittingRecordSuccess),
    tap(() => this.router.navigate(['admin']))
  ), { dispatch: false });

  showToastForSuccessfulSave$ = createEffect(() => this.actions$.pipe(
    ofType(fromStore.actions.ADMIN.saveFittingRecordSuccess),
    map(action => fromStore.actions.MESSAGES.addToast({
      toast: toastFactory({
        class: 'success',
        message: 'The Fitting record has been saved'
      })
    }))));

  addDesignCode$ = createEffect(() => this.actions$.pipe(
    ofType(fromStore.actions.ADMIN.addFittingDesignCode),
    switchMap(action => this.fittingService.addDesignCode({ fittingProductId: action.fittingId, designCodeId: action.designCodeId })
      .pipe(map(fittingDetail => fromStore.actions.ADMIN.addFittingDesignCodeSuccess({ fittingDetail })),
        catchError(error => of(fromStore.actions.ADMIN.addFittingDesignCodeFail({ error }))))
    )
  ));

  removeDesignCode$ = createEffect(() => this.actions$.pipe(
    ofType(fromStore.actions.ADMIN.removeFittingDesignCode),
    switchMap(action => this.fittingService.removeDesignCode(action.fittingProductDesignCodeId)
      .pipe(map(() => fromStore.actions.ADMIN.removeFittingDesignCodeSuccess()),
        catchError(error => of(fromStore.actions.ADMIN.removeFittingDesignCodeFail({ error })))))));

  showToastForDesignCodeAdd$ = createEffect(() => this.actions$.pipe(
    ofType(fromStore.actions.ADMIN.addFittingDesignCodeSuccess),
    map(action => fromStore.actions.MESSAGES.addToast({
      toast: toastFactory({
        class: 'success',
        message: 'Design was added successfully'
      })
    }))
  ));

  showToastForDesignCodeRemove$ = createEffect(() => this.actions$.pipe(
    ofType(fromStore.actions.ADMIN.removeFittingDesignCodeSuccess),
    map(action => fromStore.actions.MESSAGES.addToast({
      toast: toastFactory({
        class: 'success',
        message: 'Design code was removed successfully'
      })
    }))));

  addDesignPressure$ = createEffect(() => this.actions$.pipe(
    ofType(fromStore.actions.ADMIN.addFittingDesignPressure),
    switchMap(action => this.fittingService.addDesignPressure({ designFactorId: action.designFactorId, fittingProductId: action.fittingId, maxDesignPressure: action.maxPressure })
      .pipe(map(() => fromStore.actions.ADMIN.addFittingDesignPressureSuccess()),
        catchError(error => of(fromStore.actions.ADMIN.addFittingDesignPressureFail({ error })))))));

  removeDesignPressure$ = createEffect(() => this.actions$.pipe(
    ofType(fromStore.actions.ADMIN.removeFittingDesignPressure),
    switchMap(action => this.fittingService.removeDesignPressure(action.fittingProductDesignPressureId)
      .pipe(map(() => fromStore.actions.ADMIN.removeFittingDesignPressureSuccess()),
        catchError(error => of(fromStore.actions.ADMIN.removeFittingDesignPressureFail({ error })))))));


  showToastForError$ = createEffect(() => this.actions$.pipe(
    ofType(fromStore.actions.ADMIN.saveFittingRecordFail),
    map(action => fromStore.actions.MESSAGES.addToast({
      toast: toastFactory({
        class: 'danger',
        message: action.error
      })
    }))
  ));

  addErrorMessageForError$ = createEffect(() => this.actions$.pipe(
    ofType(fromStore.actions.ADMIN.saveFittingRecordFail),
    map(action => fromStore.actions.ERRORS.addGlobalError({ error: action.error }))
  ));

  addAssociatedProduct$ = createEffect(() => this.actions$.pipe(
    ofType(fromStore.actions.ADMIN.addAssociatedProduct),
    switchMap(action => this.fittingService.addAssociatedProduct({ associatedProductId: action.associateProductId, fittingProductId: action.fittingId })
      .pipe(map(res => fromStore.actions.ADMIN.addAssociatedProductSuccessful()),
        catchError(error => of(fromStore.actions.ADMIN.addAssociatedProductFail({ error })))))));

  queryAssociatedProducts$ = createEffect(() => this.actions$.pipe(
    ofType(fromStore.actions.ADMIN.queryAssociatedProducts),
    switchMap(action => this.fittingService.getFittingAssociatedProducts(odataParamsFactory({
      filter: `startsWith(productNumber, '${action.q}') OR contains(productName, '${action.q}') OR contains(description, '${action.q}')`,
      select: ['id', 'AssociatedProductTypeId', 'ProductNumber', 'ProductName', 'Description', 'CreatedDate', 'CreatedBy', 'UpdatedDate', 'UpdatedBy'],
      expand: 'associatedProductType',
      top: 5
    }))
      .pipe(map(products => fromStore.actions.ADMIN.queryAssociatedProductsSuccess({ products })),
        catchError(error => of(fromStore.actions.ADMIN.queryAssociatedProductsFail({ error })))))
  ));


  constructor(private actions$: Actions,
    private store: Store<fromStore.State>,
    private fittingService: FittingsService,
    private configurationService: ConfigurationsService,
    private monitoringService: MonitoringService,
    private router: Router) { }
}
